import { Link } from "react-router-dom";

import { pieceColorVar, type PieceType } from "@/game/pieces";
import { Tetromino } from "./Tetromino";

interface Project {
  readonly id: string;
  readonly name: string;
  /** One line under the name — what the thing is. */
  readonly blurb: string;
  readonly detail: string;
  readonly link: { readonly href: string; readonly label: string; readonly external?: boolean };
  /** Accent ink, so each project reads as its own thing on the shelf. */
  readonly piece: PieceType;
  /** A caveat worth stating on a phone, where the project may not be usable. */
  readonly smallScreenNote?: string;
}

/**
 * Things the club has built. Each entry reads as a card that links to the
 * project's own page — the shelf advertises, it does not embed. The board in
 * particular lives at /play, so it is loaded once, deliberately, by someone who
 * came to play it rather than on every visit to the home page.
 *
 * The home section and the `/projects` page both render this one list, so the
 * two surfaces cannot drift apart.
 */
const PROJECTS: readonly Project[] = [
  {
    id: "puzzles",
    name: "Puzzles",
    blurb: "The club's puzzle archive, with community difficulty ratings.",
    detail:
      "Puzzles collected from the Discord and opened in the blueprint viewer. Anyone can " +
      "browse; make an account to rate one and help settle how hard it really is.",
    link: { href: "/puzzles", label: "Browse the puzzles" },
    piece: "S",
  },
  {
    id: "board",
    name: "The board",
    blurb: "A full modern Tetris board, playable on this site.",
    detail: "No download and no account required.",
    link: { href: "/play", label: "Open the board" },
    piece: "T",
    smallScreenNote:
      "The board needs a keyboard, so it is not available on a phone. Open the site on a " +
      "computer to play.",
  },
];

function ProjectLink({ link }: { readonly link: Project["link"] }) {
  if (link.external) {
    return (
      <a className="btn" href={link.href} target="_blank" rel="noopener noreferrer">
        {link.label}
      </a>
    );
  }
  return (
    <Link to={link.href} className="btn">
      {link.label}
    </Link>
  );
}

function ProjectEntry({
  project,
  index,
}: {
  readonly project: Project;
  readonly index: number;
}) {
  const slot = String(index + 1).padStart(2, "0");

  return (
    <article
      className="slab slab__well flex h-full flex-col p-6 sm:p-7"
      style={{ ["--slab-ink" as string]: pieceColorVar(project.piece) }}
    >
      {/* The slot number and the piece, laid out like one cell of the NEXT
          queue in the rail — the same furniture the rest of the site uses to
          say "here is a piece, in order". */}
      <div className="mb-5 flex items-start justify-between gap-4">
        <span
          className="meta border-2 border-ink px-2 py-1 text-[0.6875rem] leading-none"
          style={{ background: pieceColorVar(project.piece) }}
        >
          {slot}
        </span>
        <Tetromino type={project.piece} cell={13} title={`${project.piece} piece`} />
      </div>

      <h3 className="text-[clamp(1.75rem,4vw,2.375rem)] leading-none">{project.name}</h3>
      <p className="mt-3 text-lg leading-snug font-medium">{project.blurb}</p>
      <p className="mt-3 leading-relaxed opacity-75">{project.detail}</p>

      {project.smallScreenNote ? (
        <p className="mt-5 border-2 border-ink border-dashed px-4 py-3 text-[0.875rem] leading-snug md:hidden">
          {project.smallScreenNote}
        </p>
      ) : null}

      {/* The link is offered on every screen. The note above sets expectations,
          and the project's own page repeats them — withholding it entirely
          would leave a phone with no way through at all.

          `mt-auto` sits it on the card's bottom edge, so neighbours in a row
          line their buttons up instead of each starting wherever its own
          paragraph ended. In one column there is no slack to take up, so it
          behaves exactly like a plain top margin. */}
      <div className="mt-auto flex flex-wrap gap-3 pt-7">
        <ProjectLink link={project.link} />
      </div>
    </article>
  );
}

/** How many projects are listed. */
export const PROJECT_COUNT = PROJECTS.length;

/**
 * Two across once there is room for it. The entries are short now that none of
 * them embeds anything, so stacking them one per row pushed the rest of the page
 * a long way down for no benefit.
 */
export function ProjectShelf() {
  return (
    <div className="grid gap-8 md:grid-cols-2 md:gap-10">
      {PROJECTS.map((project, index) => (
        <ProjectEntry key={project.id} project={project} index={index} />
      ))}
    </div>
  );
}
